import { ObjectTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { PropertyDefinitions } from 'hs-resources/resources/PropertyDefinition';
import { PropertyDefinition } from 'inbound-db-properties-service-client/InboundDbPropertiesService-Unified/types';
import { useResources, watch } from 'pulse/react';
import { useMemo } from 'react';
import memoizeOne from 'react-utils/memoizeOne';

import { useHttpClient } from '../../client/HttpClientContext';
import { useHttpResource } from '../../client/useHttpResource';
import { AsyncState } from '../../types/AsyncState';
import { UseAsyncFunctionOptions } from '../../utils/hooks/useAsyncFunction';
import { keyBy } from '../../utils/keyBy';
import { usePropertyDefinitionOverride } from '../context/PropertyDefinitionOverrideContext';
import { useObjectTypeId } from './useObjectTypeId';
import { useProperty } from './useProperty';
import { getOwnProperty } from '../../utils/getOwnProperty';

const defaultResourceData: PropertyDefinition[] = [];

const keyByName = memoizeOne((properties: PropertyDefinition[]) =>
  keyBy(properties, ({ property: { name } }) => name)
);

export const usePropertyDefinitionByName = (
  objectTypeId: ObjectTypeId,
  options: UseAsyncFunctionOptions = { skip: false }
) => {
  const propertyDefinitionOverride = usePropertyDefinitionOverride();

  const {
    data: resourceData = defaultResourceData,
    loading,
    error: resourceError,
  } = useHttpResource(PropertyDefinitions, {
    skip: options.skip,
    params: {
      objectTypeId,
      showHighlySensitiveProperties: true,
    },
  });

  let propertyDefinitionsByName = useMemo(
    () => keyByName(resourceData),
    [resourceData]
  );
  const error = resourceError || undefined;

  if (propertyDefinitionOverride) {
    // We need to copy here because we cannot mutate pulse resources.
    propertyDefinitionsByName = {
      ...propertyDefinitionsByName,
      [propertyDefinitionOverride.property.name]: propertyDefinitionOverride,
    };
  }

  return {
    propertyDefinitionsByName,
    error,
    loading,
  };
};

/**
 * Fetches the property definition for the current property or a specific property by name.
 */
export const usePropertyDefinition = ({
  skip,
  propertyName,
}: {
  skip?: boolean;
  propertyName?: string;
} = {}) => {
  const property = useProperty();
  const objectTypeId = useObjectTypeId();

  const {
    propertyDefinitionsByName = {},
    error,
    loading,
  } = usePropertyDefinitionByName(objectTypeId, {
    skip,
  });

  const targetPropertyName = propertyName || property.name;
  const propertyDefinition = getOwnProperty(
    propertyDefinitionsByName,
    targetPropertyName
  );

  return {
    propertyDefinition,
    error,
    loading,
  };
};

type PropertyDefinitionsByObjectTypeIdAndName = Record<
  ObjectTypeId,
  Record<string, PropertyDefinition>
>;

export const usePropertyDefinitionsByObjectTypeId = (
  objectTypeIds: ObjectTypeId[],
  options: UseAsyncFunctionOptions = { skip: false }
): AsyncState<PropertyDefinitionsByObjectTypeIdAndName> => {
  const httpClient = useHttpClient();

  const objectTypeIdsString = useMemo(
    () => objectTypeIds.join(','),
    [objectTypeIds]
  );

  const memoizedObjectIds = useMemo(
    () =>
      objectTypeIds.map((objectTypeId) =>
        watch(PropertyDefinitions, {
          httpClient,
          skip: options.skip,
          params: {
            objectTypeId,
            showHighlySensitiveProperties: true,
          },
        })
      ),
    // objectTypeIds is joined to a stable key so inline-array callers don't recompute every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [httpClient, objectTypeIdsString, options.skip]
  );
  const results = useResources(memoizedObjectIds);

  return useMemo(() => {
    if (options.skip) {
      return { loading: false, data: undefined, error: undefined };
    }

    if (results.some((result) => result.loading)) {
      return { loading: true, data: undefined, error: undefined };
    }

    const error = results.find((result) => result.error)?.error;
    if (error) {
      return { loading: false, data: undefined, error };
    }

    const data = objectTypeIds.reduce((acc, objectTypeId, index) => {
      acc[objectTypeId] = keyByName(results[index].data ?? defaultResourceData);
      return acc;
    }, {} as PropertyDefinitionsByObjectTypeIdAndName);

    return { loading: false, data, error: undefined };
    // objectTypeIds is joined to a stable key so inline-array callers don't recompute every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectTypeIdsString, options.skip, results]);
};
